// src/stores/Auth/auth.ts
import { defineStore } from 'pinia'
import api, { applyTokens, clearTokens } from '@/axios/http'

type User = {
  id: number
  firstname: string
  lastname: string
  email: string
  phone?: string | null
  image?: string | null
}

type LoginResp = {
  user?: User
  access_token: string
  refresh_token?: string
}

interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

const memoryStore = new Map<string, string>()
const memoryStorage: StorageLike = {
  getItem: (k) => (memoryStore.has(k) ? memoryStore.get(k)! : null),
  setItem: (k, v) => void memoryStore.set(k, v),
  removeItem: (k) => void memoryStore.delete(k),
}

// const safeStorage: StorageLike =
//   typeof window !== 'undefined' && window.localStorage ? window.localStorage : memoryStorage

// src/stores/Auth/auth.ts

// Replace the safeStorage definition with sessionStorage
const safeStorage: StorageLike =
  typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : memoryStorage

// Storage keys (centralized)
const ACCESS_KEY = 'access_token'
const REFRESH_KEY = 'refresh_token'
const PERMS_KEY  = 'perms'
const USER_KEY   = 'auth_user_v1'

// ---- tiny bit-flags helper you can re-use in components/route-guards
export enum Perm { VIEW=1, CREATE=2, EDIT=4, DELETE=8, PUBLISH=16 }
export const has = (mask: number | undefined, perm: Perm) =>
  typeof mask === 'number' && (mask & perm) === perm

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: (() => {
      try { return JSON.parse(safeStorage.getItem(USER_KEY) || 'null') as User | null }
      catch { return null }
    })(),
    accessToken: (safeStorage.getItem(ACCESS_KEY) || '') as string,
    refreshToken: (safeStorage.getItem(REFRESH_KEY) || '') as string,
    perms: JSON.parse(safeStorage.getItem(PERMS_KEY) || '{}') as Record<string, number>,
    loading: false,
    error: '' as string,
    hydrated: false, // 👈 important: router should wait for this before redirecting
  }),

  getters: {
    isAuthenticated: (s) => !!s.accessToken,
  },

  actions: {
    /* ------------------ internal helpers ------------------ */
    _saveTokens() {
      if (this.accessToken) safeStorage.setItem(ACCESS_KEY, this.accessToken)
      else safeStorage.removeItem(ACCESS_KEY)

      if (this.refreshToken) safeStorage.setItem(REFRESH_KEY, this.refreshToken)
      else safeStorage.removeItem(REFRESH_KEY)
    },
    _saveUser() {
      if (this.user) safeStorage.setItem(USER_KEY, JSON.stringify(this.user))
      else safeStorage.removeItem(USER_KEY)
    },
    _savePerms() {
      if (this.perms) safeStorage.setItem(PERMS_KEY, JSON.stringify(this.perms))
      else safeStorage.removeItem(PERMS_KEY)
    },
    _clearAll() {
      this.user = null
      this.accessToken = ''
      this.refreshToken = ''
      this.perms = {}
      clearTokens()
      safeStorage.removeItem(ACCESS_KEY)
      safeStorage.removeItem(REFRESH_KEY)
      safeStorage.removeItem(PERMS_KEY)
      safeStorage.removeItem(USER_KEY)
    },

    /* ------------------ hydration on app boot ------------------ */
    // call once on app boot (e.g., in main.ts or in a router guard) to restore axios headers + refresh perms
    async initFromStorage() {
      if (this.hydrated) return
      // Re-apply headers for Axios/interceptors
      if (this.accessToken) {
        applyTokens(this.accessToken, this.refreshToken || undefined)
      }
      // Optionally, fetch user if we have a token but no user cached
      if (this.isAuthenticated && !this.user) {
        try { await this.fetchMe() } catch { /* token may be expired; leave user null */ }
      }
      // Refresh perms (tolerate failure)
      if (this.isAuthenticated) {
        try { await this.refreshPermissions() } catch { /* ignore */ }
      }
      this.hydrated = true
    },

    /* ------------------ auth flows ------------------ */
    async login(email: string, password: string): Promise<boolean> {
      this.error = ''
      this.loading = true
      try {
        const { data } = await api.post<LoginResp>('/auth/login', { email, password })
        if (!data?.access_token) {
          this.error = 'No access token returned'
          this._clearAll()
          return false
        }

        // Persist for interceptors & storage
        applyTokens(data.access_token, data.refresh_token)
        this.accessToken = data.access_token
        this.refreshToken = data.refresh_token ?? ''
        this._saveTokens()

        // Save user immediately if server returned it (avoids flicker after login)
        if (data.user) {
          this.user = data.user
          this._saveUser()
        } else {
          // Otherwise fetch profile
          try { await this.fetchMe() } catch { /* ignore */ }
        }

        // Fetch permissions map right after login
        await this.refreshPermissions()

        this.hydrated = true
        return true
      } catch (e: any) {
        this.error = e?.response?.data?.message ?? e?.message ?? 'Login failed'
        this._clearAll()
        return false
      } finally {
        this.loading = false
      }
    },

    async refreshPermissions() {
      const { data } = await api.get<Record<string, number>>('/rbac/my-permissions')
      this.perms = data || {}
      this._savePerms()
      return this.perms
    },

    async fetchMe(): Promise<User> {
      const { data } = await api.get<User>('/auth/me')
      this.user = data
      this._saveUser()
      return data
    },

    async logout(): Promise<void> {
      try { await api.post('/auth/logout') } catch { /* ignore */ }
      finally { this._clearAll() }
    },

 // in src/stores/Auth/auth.ts (inside actions)
async logoutKeepalive(params?: { reason?: string }) {
  const url = `${api.defaults.baseURL ?? ''}/auth/logout`
  const body = JSON.stringify(params ?? { reason: 'auto' })

  // clear locally immediately so UI is logged out
  this._clearAll()

  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' })
      navigator.sendBeacon(url, blob)  // survives tab close
      return
    }
    await fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body,
    }).catch(() => undefined)
  } catch {
    // ignore
  }
}

  },
})
